import type { HandlingUnit } from "~/shared/types/quote-details-types";

const isPositive = (value: number | null | undefined): boolean =>
	value != null && value > 0;

const isBlank = (value: string | null | undefined): boolean =>
	value == null || value.trim() === "";

const hasSelectedValue = (
	value?: { id?: string | number; name?: string; code?: string } | null,
): boolean => {
	if (!value) {
		return false;
	}
	if (value.id != null && Number(value.id) > 0) {
		return true;
	}
	return !isBlank(value.code) || !isBlank(value.name);
};

/**
 * Validates a freight commodity handling unit and returns the set of invalid
 * field keys (matching the field keys used by the editor). Quantity, weight,
 * dimensions, unit type and class are always required; piece count is required
 * when configured (hazmat or Sort and Segregate), and hazmat detail fields are
 * required when the item is hazmat.
 */
export const validateFreightCommodity = (
	item: HandlingUnit,
	options?: { requirePieceCount?: boolean },
): Set<string> => {
	const isClassInvalid = !item.class || item.class.id === 0 || !item.class.name;
	const isUnitInvalid = !item.unit?.id || !item.unit?.name;
	const requirePieceCount = options?.requirePieceCount ?? !!item.isHazmat;

	const checks: [string, boolean][] = [
		["quantity", !isPositive(item.quantity)],
		["weight", !isPositive(item.weight)],
		["length", !isPositive(item.length)],
		["width", !isPositive(item.width)],
		["height", !isPositive(item.height)],
		["unit", isUnitInvalid],
		["class", isClassInvalid],
		["pieceCount", requirePieceCount && !isPositive(item.pieceCount)],
	];

	if (item.isHazmat) {
		checks.push(
			["unNumber", isBlank(item.unNumber)],
			["properShippingName", isBlank(item.properShippingName)],
			["hazmatClass", !hasSelectedValue(item.hazmatClass)],
			["packingGroupId", !hasSelectedValue(item.packingGroupId)],
		);
	}

	return new Set(
		checks.filter(([, isInvalid]) => isInvalid).map(([key]) => key),
	);
};
